import { useQuery } from "@tanstack/react-query";

export interface WindGridPoint {
  lat: number;
  lon: number;
  u: number;
  v: number;
}

/** One hour's worth of U/V wind vectors across the Sweden grid. */
export type WindGridSlice = WindGridPoint[];

/** Open-Meteo returns an array of location objects when multiple lat/lon are passed. */
interface OpenMeteoLocation {
  latitude: number;
  longitude: number;
  hourly: {
    time: string[];
    wind_u_component_10m: number[];
    wind_v_component_10m: number[];
  };
}

/**
 * Generate a regular lat/lon grid covering Sweden.
 * 1.0° resolution → ~16×16 = 256 points — good balance of detail vs URL length.
 */
function buildGrid(): { lats: number[]; lons: number[] } {
  const lats: number[] = [];
  const lons: number[] = [];
  for (let lat = 55.0; lat <= 71.0; lat += 1.0) lats.push(Math.round(lat * 10) / 10);
  for (let lon = 10.0; lon <= 26.0; lon += 1.0) lons.push(Math.round(lon * 10) / 10);
  return { lats, lons };
}

const GRID = buildGrid();

async function fetchWindGrid(): Promise<WindGridSlice[]> {
  // Flatten the grid into parallel lat/lon arrays (cartesian product).
  const flatLats: number[] = [];
  const flatLons: number[] = [];
  for (const lat of GRID.lats) {
    for (const lon of GRID.lons) {
      flatLats.push(lat);
      flatLons.push(lon);
    }
  }

  const params = new URLSearchParams({
    latitude: flatLats.join(","),
    longitude: flatLons.join(","),
    hourly: "wind_u_component_10m,wind_v_component_10m",
    forecast_days: "2",
    wind_speed_unit: "ms",
  });

  const res = await fetch(
    `https://api.open-meteo.com/v1/forecast?${params.toString()}`,
  );
  if (!res.ok) throw new Error(`Open-Meteo error: ${res.status}`);

  // When multiple lat/lon pairs are passed, the API returns an array of
  // location objects, each with its own hourly arrays.
  const locations = (await res.json()) as OpenMeteoLocation[];

  if (!Array.isArray(locations) || locations.length === 0) {
    throw new Error("Open-Meteo returned unexpected format");
  }

  // All locations share the same time axis — grab it from the first one.
  const times = locations[0]?.hourly.time ?? [];
  const hourCount = times.length;

  // Build one WindGridSlice per hour.
  const slices: WindGridSlice[] = Array.from({ length: hourCount }, (_, hourIdx) => {
    return locations.map((loc) => ({
      lat: loc.latitude,
      lon: loc.longitude,
      u: loc.hourly.wind_u_component_10m[hourIdx] ?? 0,
      v: loc.hourly.wind_v_component_10m[hourIdx] ?? 0,
    }));
  });

  return slices;
}

export function useWindGrid() {
  return useQuery({
    queryKey: ["windGrid"],
    queryFn: fetchWindGrid,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
}

/**
 * Returns the grid slice index for the current UTC hour, clamped to [0, 47].
 * Useful as the default hour when the scrubber mounts.
 */
export function currentHourIndex(): number {
  return Math.min(new Date().getUTCHours(), 47);
}
